/**
 * Paste a screenshot into a file field: one rule for every file field in the command shell. With the field focused
 * (the file box, or its Replace or Add files button), Ctrl+V (or ⌘V) takes an image off the clipboard as if the
 * person had chosen it, named "Pasted screenshot {date time}.png". The field says so under itself ("or paste a
 * screenshot") and the hint is part of the box's description, so a screen reader hears it with the field.
 *
 * A field that takes no images (a CSV, a PDF only) offers nothing, and a paste of text is left to the browser. What
 * a paste carries is read through the platform's `pastedImage` port, not here.
 */
import type { ClipboardEvent, ReactNode } from "react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { currentTime } from "@shared-core/domain/current-time";
import type { ChosenFile } from "@shared-core/domain/object-ui/contract";
import {
  acceptsPastedImage,
  pastedScreenshotName,
} from "@shared-core/domain/object-ui/upload-field";
import { Input } from "../../primitives/input";

export const PASTE_HINT = "or paste a screenshot";

export interface PasteScreenshot {
  /** Put on the element wrapping the field, so a paste from anything inside it is heard. */
  onPaste: (event: ClipboardEvent) => void;
  /** The hint under the field, or null where the field takes no images. */
  hint: ReactNode;
  /** The hint's id for `aria-describedby`, or undefined where there is no hint. */
  hintId: string | undefined;
}

export function usePasteScreenshot({
  id,
  accept,
  disabled,
  onFile,
}: {
  id: string;
  accept: readonly string[] | null;
  disabled: boolean;
  /** Called with the pasted image as a chosen file. */
  onFile: (file: ChosenFile) => void;
}): PasteScreenshot {
  const capabilities = usePlatformCapabilities();
  const offersPaste = !disabled && acceptsPastedImage(accept, "image/png");
  const hintId = offersPaste ? `${id}-paste-hint` : undefined;
  return {
    onPaste: (event) => {
      if (!offersPaste) return;
      const image = capabilities.pastedImage(event);
      if (!image || !acceptsPastedImage(accept, image.type)) return;
      event.preventDefault();
      onFile({
        kind: "chosen_file",
        name: pastedScreenshotName(currentTime(), image.type),
        size: image.size,
        blob: image,
      });
    },
    hint: offersPaste ? (
      <p id={hintId} className="text-xs text-muted-foreground">
        {PASTE_HINT}
      </p>
    ) : null,
    hintId,
  };
}

/** The field's invalid props with the paste hint added to what describes it. */
export function describedByHint(
  invalid: Record<string, unknown>,
  hintId: string | undefined,
): Record<string, unknown> {
  if (!hintId) return invalid;
  const existing = invalid["aria-describedby"];
  return {
    ...invalid,
    "aria-describedby":
      typeof existing === "string" && existing
        ? `${existing} ${hintId}`
        : hintId,
  };
}

/** A file field that holds the chosen file itself (the command uploads it with its body). */
export function PlainFileField({
  id,
  value,
  disabled,
  invalid,
  onChange,
}: {
  id: string;
  value: ChosenFile | null;
  disabled: boolean;
  invalid: Record<string, unknown>;
  onChange: (file: ChosenFile | null) => void;
}) {
  const paste = usePasteScreenshot({
    id,
    accept: null,
    disabled,
    onFile: onChange,
  });
  return (
    <div className="space-y-1" onPaste={paste.onPaste}>
      <Input
        id={id}
        type="file"
        disabled={disabled}
        {...describedByHint(invalid, paste.hintId)}
        onChange={(e) => {
          const file = e.target.files?.[0];
          onChange(
            file
              ? {
                  kind: "chosen_file",
                  name: file.name,
                  size: file.size,
                  blob: file,
                }
              : null,
          );
        }}
      />
      {paste.hint}
      {value && <p className="text-xs text-muted-foreground">{value.name}</p>}
    </div>
  );
}
